import React, { useState } from 'react';
import { CreatePlanRequest } from '../services/planService';

interface CreatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePlanRequest) => Promise<void>;
  loading?: boolean;
}

export const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreatePlanRequest>({
    planName: '',
    amount: 0,
    currency: 'USD',
    features: [],
    validity: 1,
    description: '',
  });
  const [amountInput, setAmountInput] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.planName.trim()) {
      newErrors.planName = 'Plan name is required';
    } else if (formData.planName.length > 100) {
      newErrors.planName = 'Plan name must be 100 characters or less';
    }

    if (formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (formData.validity < 1) {
      newErrors.validity = 'Validity must be at least 1 month';
    }

    if (!/^[A-Z]{3}$/.test(formData.currency)) {
      newErrors.currency = 'Currency must be a 3-letter ISO code';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        ...formData,
        features: formData.features.map(feature => feature.trim()).filter(Boolean),
      });
      handleClose();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    setFormData({
      planName: '',
      amount: 0,
      currency: 'USD',
      features: [],
      validity: 1,
      description: '',
    });
    setAmountInput('');
    setErrors({});
    onClose();
  };

  const handleInputChange = (field: keyof CreatePlanRequest, value: string | number | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmountInput(value);
    // Only update formData if there's a valid number
    if (value === '' || value === '.') {
      handleInputChange('amount', 0);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        handleInputChange('amount', numValue);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md sm:max-w-lg p-4 sm:p-5 border shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Create New Payment Plan
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Plan Name */}
            <div>
              <label htmlFor="planName" className="block text-sm font-medium text-gray-700 mb-1">
                Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="planName"
                value={formData.planName}
                onChange={(e) => handleInputChange('planName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.planName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Monthly Plan"
                maxLength={100}
                disabled={loading}
              />
              {errors.planName && (
                <p className="mt-1 text-sm text-red-600">{errors.planName}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">{formData.planName.length}/100 characters</p>
            </div>

            {/* Amount and Validity Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Amount */}
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="amount"
                  value={amountInput}
                  onChange={handleAmountChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.amount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  disabled={loading}
                />
                {errors.amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
                )}
              </div>

              {/* Currency */}
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Currency <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value.toUpperCase())}
                  maxLength={3}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.currency ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="USD"
                  disabled={loading}
                />
                {errors.currency && <p className="mt-1 text-sm text-red-600">{errors.currency}</p>}
              </div>

              {/* Validity */}
              <div>
                <label htmlFor="validity" className="block text-sm font-medium text-gray-700 mb-1">
                  Validity (Months) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="validity"
                  value={formData.validity}
                  onChange={(e) => handleInputChange('validity', parseInt(e.target.value) || 1)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.validity ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="1"
                  min="1"
                  step="1"
                  disabled={loading}
                />
                {errors.validity && (
                  <p className="mt-1 text-sm text-red-600">{errors.validity}</p>
                )}
              </div>
            </div>

            {/* Features */}
            <div>
              <label htmlFor="features" className="block text-sm font-medium text-gray-700 mb-1">
                Features <span className="text-gray-500">(one per line)</span>
              </label>
              <textarea
                id="features"
                value={formData.features.join('\n')}
                onChange={(e) => handleInputChange('features', e.target.value.split('\n'))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={'Priority support\nReduced verification fee'}
                rows={3}
                disabled={loading}
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-gray-500">(Optional)</span>
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Describe the plan features and benefits..."
                rows={4}
                maxLength={500}
                disabled={loading}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">{formData.description?.length || 0}/500 characters</p>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Create Plan'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
